import moment from 'moment';
import { zonedTimeToUtc } from 'date-fns-tz';
import { parse } from "date-fns";
import FontAwesome from 'react-fontawesome';
import React from 'react';
import { connect } from 'react-redux';
import { Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { routerActions } from 'react-router-redux'
import { entity as communicationPlan } from '../../ducks/communicationPlans';
import { entity as field } from '../../ducks/fields';
import { actions as editorActions, ruleFieldsQuery, ruleFieldsKey } from '../../ducks/communicationPlanEditor';
import { customers as customersDuck, modals } from '../../ducks/index';
import CommunicationPlanEditor from './CommunicationPlanEditor';
import ManagedResourceList from '../shared/ManagedResourceList';
import { stringifyCriteria } from 'utils';
import ModalComponent from "../ModalComponent";
import ModalRoot from "../ModalRoot";
import PageHeader from '../PageHeader';

import './styles/communicationPlanList.scss';
import { singleDuck } from 'utils/duckify';

const humanizeStatus = plan => plan.contact_status.replace(/_/g, ' ');

const action = (plan) => {
  switch (plan.type) {
    case 'ReminderCommunicationPlan':
      return 'Create Reminder';
    case 'EmailCommunicationPlan':
      return 'Send Email';
    default:
      return 'n/a';
  }
};

const planDescription = (plan) => {
  const desc = plan.checklist_item ?
    `completes ${plan.checklist_item.disambiguated_name} checklist item` :
    `enters ${humanizeStatus(plan)} status`;
  return `${action(plan)} when contact ${desc}`;
};

const PlanSchedule = ({ item, customer }) => {
  let desc;
  let expired = false;

  switch (item.schedule_type) {
    case 'immediate':
      desc = "Immediately upon trigger";
      break;
    case 'num_days':
      desc = '' + item.schedule_num + ' days after trigger at ' + item.formatted_local_time;
      break;
    case 'date':
      let scheduleDate = moment(item.schedule_date);
      desc = 'On ' + scheduleDate.format(global.I18n.date.formats.default_moment) + ' at ' + item.formatted_local_time;
      // Create a datetime in the customer timezone for comparison
      const dateTime = parse(item.schedule_date + ' ' + item.formatted_local_time, 'yyyy-MM-dd hh:mm a', 0);
      const combinedDateTime = zonedTimeToUtc(dateTime, customer.value.time_zone);

      expired = moment(combinedDateTime).utc().isBefore(moment().utc(), 'minutes');
      break;
    default:
      desc = 'n/a';
  }

  return <div className={expired ? 'communication-plan-schedule-expired' : ''}>
    {desc}
    {expired && ' expired'}
  </div>;
};

const PlanTrigger = ({ item }) =>
  (item.checklist_item)
    ? (<span className="communication-plan-trigger">Complete <em>{item.checklist_item.disambiguated_name}</em> checklist item</span>)
    : (<span className="communication-plan-trigger">Enter <em>{humanizeStatus(item)}</em> status</span>);

// Renders children in an <OverlayTrigger/> if the `overflow` property is set
const OverflowPopup = ({ overflow, children, ...overlayProps }) => {
  return overflow ?
    <OverlayTrigger {...overlayProps}>{children}</OverlayTrigger> :
    <div>{children}</div>
};

class PlanCriteria extends React.Component {
  constructor() {
    super();
    this.state = { overflow: false };
    this.checkOverflow = this.checkOverflow.bind(this);
  }

  componentDidMount() {
    this.checkOverflow();
  }

  componentDidUpdate() {
    this.checkOverflow();
  }

  checkOverflow() {
    const oldOverflow = this.state.overflow;
    const newOverflow = this.el.clientWidth < this.el.scrollWidth;
    if (oldOverflow !== newOverflow) {
      this.setState({ overflow: newOverflow });
    }
  }

  render() {
    const { item, fieldsByName } = this.props;
    const { overflow } = this.state;

    if (fieldsByName.pending) {
      // Render placeholder if field definitions are still loading
      return <div className="communication-plan-criteria" ref={el => this.el = el}>
        {item.criteria && <code>{'...'}</code>}
      </div>
    } else {
      const criteria = stringifyCriteria(item.criteria, name => fieldsByName.get(name));
      const tooltip = <Tooltip id="tooltip">{criteria}</Tooltip>;

      return <OverflowPopup overflow={overflow} placement="right" overlay={tooltip}>
        <div className="communication-plan-criteria" ref={el => this.el = el}>
          {criteria && <code style={{ cursor: overflow ? 'default' : 'auto' }}>{criteria}</code>}
        </div>
      </OverflowPopup>;
    }
  }
}

const ConfirmDeactivateModal = connect()(({ dispatch, item }) => {
  const buttons = [
    <button className="btn btn-danger"
      onClick={() => {
        dispatch(modals.actions.hide());
        dispatch(editorActions.save({ id: item.id, active: false }))
          .catch(() => dispatch(modals.actions.show('ActivateErrorModal')));
      }}>Deactivate</button>
  ];
  return (
    <ModalComponent title="Confirm Deactivate"
      buttons={buttons}
      closeText="Cancel"
      onClose={() => dispatch(modals.actions.hide())}>
      <p>Are you sure you want to deactivate <em>{item.id ? planDescription(item) : ''}</em>?</p>
      <p>This will cancel any pending communications that have been scheduled for this step.</p>
    </ModalComponent>
  );
});

const ActivateErrorModal = connect()(({ dispatch }) => {
  return (
    <ModalComponent title={'Activation Error'}
      onClose={() => dispatch(modals.actions.hide())}>
      A configuration error is preventing this step from being activated or deactivated. Please edit the step to
      correct the errors and try again.
    </ModalComponent>
  );
});

const Activate = ({ active, pending, onClick }) => {
  return (
    <div>
      {pending ?
        <FontAwesome name="spinner" spin /> :
        <button className="communication-plan-activate-button btn btn-sm" onClick={onClick}>
          {active ?
            <FontAwesome name='check' /> :
            <div style={{ width: '1em' }}>&nbsp;</div>}
        </button>}
    </div>
  );
};

class CommunicationPlanList extends React.Component {

  constructor() {
    super();

    this.toggleActive = this.toggleActive.bind(this);
  }

  componentDidMount() {
    const { dispatch } = this.props;

    dispatch(communicationPlan.actions.load());

    if (!this.props.fieldsByName.size) {
      dispatch(field.actions.load(ruleFieldsQuery, ruleFieldsKey));
    }
  }

  toggleActive(item) {
    const { dispatch } = this.props;
    if (!item.active) {
      dispatch(editorActions.save({ id: item.id, active: true }))
        .catch(() => dispatch(modals.actions.show('ActivateErrorModal')));
    } else {
      dispatch(modals.actions.show('ConfirmDeactivateModal', { item }));
    }
  }

  render() {
    const { dispatch, saving, items, fieldsByName, customer } = this.props;

    const defaultPlan = {
      workflow: this.props.workflow,
      schedule_type: 'immediate'
    };

    const filteredItems = items
      ? this.props.items.filter(item => item.workflow === this.props.workflow)
      : [];

    return (
      <div className="communication-plan-list">
        <PageHeader header="Communication Plan" />

        {items.pending && <div><FontAwesome name="spinner" spin /> Loading...</div>}

        {items.ready &&
          <div>
            <Nav variant="pills"
              activeKey={this.props.workflow}
              onSelect={key => dispatch(routerActions.push(`/setup/communication_plans/${key}`))}>
              <Nav.Item>
                <Nav.Link eventKey="inquiry">Inquiry</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="applicant">Admissions</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="enrollment">Enrollment</Nav.Link>
              </Nav.Item>
            </Nav>

            <ManagedResourceList
              fetchItems={() => Promise.resolve}
              items={filteredItems}
              displayType="step"
              emptyListMessage="Sorry, no steps are configured for this plan. Add a new step by clicking the link above."
              columns={[
                { header: 'Name', value: item => item.name },
                { header: 'Trigger', value: item => <PlanTrigger item={item} /> },
                { header: 'Additional Criteria', value: item => <PlanCriteria item={item} fieldsByName={fieldsByName} /> },
                { header: 'Timing', value: item => <PlanSchedule customer={customer} item={item} /> },
                { header: 'Action', value: action },
                { header: 'Last Edited By', value: item => item.last_edited_by },
                {
                  header: 'Active', value: item =>
                    <Activate active={item.active}
                      pending={saving[item.id]}
                      onClick={() => this.toggleActive(item)} />
                }
              ]}
              editModal={CommunicationPlanEditor}
              editModalProps={{ workflow: this.props.workflow }}
              loadItemForEdit={item => dispatch(editorActions.load({ ...defaultPlan, ...item }))}
              deleteItem={item => dispatch(communicationPlan.actions.remove(item))}
              deleteConfirmBody={item =>
                <div>
                  <p>Are you sure you want to delete <em>{planDescription(item)}</em>?</p>
                  <p>This will cancel any pending communications that have been scheduled for this step.</p>
                </div>}
              tableClassName="table-striped"
              rowClassName={item => item.active ? 'communication-plan-active' : 'communication-plan-inactive'}
            />
          </div>}

        <ModalRoot modals={{ ActivateErrorModal, ConfirmDeactivateModal }} />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const customerDuck = singleDuck(customersDuck, 'current');
  return {
    items: communicationPlan.all(state),
    customer: customerDuck.single(state, 'current'),
    fieldsByName: field.allByName(state, ruleFieldsKey),
    saving: state.communicationPlanEditor.saving,
  };
};

export default connect(mapStateToProps)(CommunicationPlanList);
